'use client';

import { Box, Button, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, useId } from 'react';
import { SteppingControl } from './SteppingControl';
import { useBannerStepping } from './useBannerStepping';
import {
  resolveRequestBannerStepperCopy,
  type RequestBannerStepperCopy,
  type RequestBannerStepperCopyOverrides,
} from './requestBannerStepperCopy';

export type { RequestBannerStepperCopy, RequestBannerStepperCopyOverrides };

export type RequestBannerStepperProps = {
  /** Which open request is shown, counted from 1. */
  step: number;
  /** How many requests are open. */
  total: number;
  /** A typed request names an entity type. A Legacy request asks for any identity. */
  kind: 'typed' | 'legacy';
  /** Who is asking. */
  providerName: string;
  /** What a typed request asks for, such as an entity type's name. Not used on a Legacy step. */
  asksLabel?: string;
  /**
   * The customer asked to see another request: the back and next buttons, a dot, an arrow key
   * while focus is in the banner, or (on a touch screen or with a pen) a swipe. `nextStep` counts
   * from 1, and the banner has already wrapped it, so next from the last request is 1 and back
   * from the first is `total`. The parent sets `step` (and the request's `kind`, `providerName`
   * and `asksLabel`) from it. With one open request there is nothing to step to: the banner shows
   * no back, next or dots, and never calls this.
   */
  onStep: (nextStep: number) => void;
  /** The primary button: Start on a typed step, Share on a Legacy step. */
  onAction: () => void;
  /** Replaces any of the default strings. Strings left out keep their default. */
  copy?: RequestBannerStepperCopyOverrides;
  'data-testid'?: string;
};

type AsksLineProps = {
  kind: RequestBannerStepperProps['kind'];
  asksLabel: string | undefined;
  copy: Pick<RequestBannerStepperCopy, 'asksFor' | 'legacyAsks'>;
};

// A Legacy request asks for any identity, so it has its own line. A typed request names its type,
// and shows nothing when the parent has no type to name.
function AsksLine({ kind, asksLabel, copy }: Readonly<AsksLineProps>): ReactElement | null {
  if (kind === 'legacy') {
    return (
      <Typography variant="body1" component="p">
        {copy.legacyAsks}
      </Typography>
    );
  }
  if (!asksLabel) {
    return null;
  }
  return (
    <Typography variant="body1" component="p">
      {copy.asksFor}{' '}
      <Box
        component="strong"
        sx={(theme: Theme) => ({ fontWeight: theme.semantic.fontWeight.semibold })}
      >
        {asksLabel}
      </Box>
    </Typography>
  );
}

// The board draws the action button 44px high.
const actionButtonSx = (theme: Theme) => ({ minHeight: theme.requestActions.buttonHeight });

// The body is a row: the text takes what the actions leave and wraps. Below `md` it is a column:
// the primary sits under the text and takes the full width.
const bodySx = (theme: Theme) => ({
  // A swipe across the request steps. A swipe up or down still scrolls the page.
  touchAction: 'pan-y',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.semantic.spacing.base,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
});

const actionsSx = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  gap: theme.semantic.spacing.sm,
  [theme.breakpoints.down('md')]: {
    alignSelf: 'stretch',
    '& > *': { flex: 1 },
  },
});

/**
 * The Home banner for the customer's open requests, one request at a time. It shows who is asking
 * and what for, a "n of m" counter, a primary button (Start on a typed step, Share on a Legacy
 * step) and, when more than one request is open, a stepping control under the request: back and
 * next either side of the dots. Both wrap, so a passed request comes back. A dot jumps to its
 * request. Arrow keys step while focus is in the banner, and a swipe steps on a touch screen.
 *
 * Presentational: the banner holds no step. The parent passes the step and sets it from
 * `onStep`. The counter is a polite live region, so a screen reader announces the new number
 * however the customer stepped. The primary is described by who is asking and what for.
 */
function RequestBannerStepperComponent({
  step,
  total,
  kind,
  providerName,
  asksLabel,
  onStep,
  onAction,
  copy: copyOverrides,
  'data-testid': dataTestId,
}: Readonly<RequestBannerStepperProps>): ReactElement {
  const copy = resolveRequestBannerStepperCopy(copyOverrides);
  const labelId = useId();
  const requestId = useId();
  const primaryLabel = { typed: copy.start, legacy: copy.share }[kind];
  const { rootRef, currentDotRef, stepBy, stepTo, onKeyDown, swipeHandlers } = useBannerStepping({
    step,
    total,
    onStep,
  });

  return (
    <Box
      component="section"
      ref={rootRef}
      aria-labelledby={labelId}
      data-testid={dataTestId}
      // Not a tab stop. A click on the banner's text puts focus on it, so the arrow keys work there.
      tabIndex={-1}
      onKeyDown={onKeyDown}
      sx={(theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        gap: theme.semantic.spacing.sm,
        padding: `${theme.semantic.spacing.base} ${theme.semantic.spacing.lg}`,
        borderRadius: theme.semantic.borderRadius.lg,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[4],
      })}
    >
      <Box
        sx={(theme: Theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: theme.semantic.spacing.sm,
        })}
      >
        <Typography id={labelId} variant="body2" component="span">
          {copy.openRequests}
        </Typography>
        <Typography
          role="status"
          variant="body2"
          component="span"
          sx={(theme: Theme) => ({
            fontFamily: theme.requestBanner.counterFontFamily,
            fontWeight: theme.semantic.fontWeight.semibold,
          })}
        >
          {copy.counter(step, total)}
        </Typography>
      </Box>
      <Box sx={bodySx} {...swipeHandlers}>
        <Box
          id={requestId}
          sx={(theme: Theme) => ({
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minWidth: 0,
            gap: theme.semantic.spacing['2xs'],
          })}
        >
          <Typography variant="h4" component="p" sx={{ overflowWrap: 'anywhere' }}>
            {providerName}
          </Typography>
          <AsksLine kind={kind} asksLabel={asksLabel} copy={copy} />
        </Box>
        <Box sx={actionsSx}>
          <Button
            variant="contained"
            size="large"
            sx={actionButtonSx}
            data-action="banner-action"
            aria-describedby={requestId}
            onClick={onAction}
          >
            {primaryLabel}
          </Button>
        </Box>
      </Box>
      {total > 1 && (
        <SteppingControl
          step={step}
          total={total}
          backLabel={copy.back}
          nextLabel={copy.next}
          dotLabel={copy.dotLabel}
          onBack={() => stepBy(-1)}
          onNext={() => stepBy(1)}
          onJump={stepTo}
          currentDotRef={currentDotRef}
        />
      )}
    </Box>
  );
}

export const RequestBannerStepper = withClarityTheme(RequestBannerStepperComponent);
